/**
 * Mapping utilities between ReportsSidebar options and URL paths.
 *
 * Each sidebar option corresponds to a unique route under /reports:
 *   - All Reports (default) → /reports
 *   - Recently Added        → /reports/recently-added
 *   - Needs Review          → /reports/needs-review
 *   - Report type           → /reports/type/:reportTypeSlug
 * These helpers convert between the `ReportsSidebarOption` union type and the
 * URL pathname so the sidebar selection is always reflected in the browser URL.
 */

import { REPORT_TYPES, ReportType } from "@/services/reportsService"

/** A selectable entry in the Reports sidebar. */
export type ReportsSidebarOption =
  | "all-reports"
  | "recently-added"
  | "needs-review"
  | `type-${ReportType}`

/** The sidebar option shown at the bare /reports path. */
export const DEFAULT_REPORTS_SIDEBAR_OPTION: ReportsSidebarOption = "all-reports"

/** Base path for the reports section. */
const BASE_PATH = "/reports"

/**
 * Path of the Needs Review view (reports with no reportType). Exported so the
 * router can redirect the retired /reports/type/unclassified and
 * /reports/type/non-report URLs here.
 */
export const NEEDS_REVIEW_PATH = `${BASE_PATH}/needs-review`

/**
 * Report-type URLs from before Needs Review replaced the separate Unclassified
 * and Non-Report Records views. The router redirects each to NEEDS_REVIEW_PATH
 * so old links and bookmarks keep working.
 */
export const RETIRED_NEEDS_REVIEW_PATHS: readonly string[] = [
  `${BASE_PATH}/type/unclassified`,
  `${BASE_PATH}/type/non-report`,
]

/** Prefix that marks a report-type sidebar option (e.g. "type-householdSurvey"). */
const TYPE_OPTION_PREFIX = "type-"

/**
 * Report type → URL slug.
 * URL: /reports/type/:reportTypeSlug
 * Spelled out (not derived by case conversion) so every slug is greppable.
 * Typed as a full Record, so adding a value to REPORT_TYPES without a slug
 * here is a compile error.
 */
export const REPORT_TYPE_TO_SLUG: Record<ReportType, string> = {
  globalReport: "global-report",
  plan: "plan",
  householdSurvey: "household-survey",
  surveillance: "surveillance",
  healthStatistics: "health-statistics",
  institutionalReport: "institutional-report",
  research: "research",
}

/**
 * URL slug → report type (reverse of REPORT_TYPE_TO_SLUG).
 * A Map rather than a plain object so that slugs such as "constructor" or
 * "toString" cannot resolve to inherited Object prototype members.
 */
export const SLUG_TO_REPORT_TYPE: ReadonlyMap<string, ReportType> = new Map(
  REPORT_TYPES.map((value: ReportType): [string, ReportType] => [REPORT_TYPE_TO_SLUG[value], value]),
)

/** Set of valid report types, used to validate the suffix of a "type-" option. */
const REPORT_TYPE_VALUE_SET: ReadonlySet<string> = new Set<string>(REPORT_TYPES)

/**
 * Extracts the report type from a report-type sidebar option.
 *
 * @param option - The sidebar option to inspect.
 * @returns The report type (e.g. "householdSurvey") for a type option, or null
 *   for "all-reports", "recently-added" and "needs-review".
 * @throws Error if the option has the "type-" prefix but an unknown suffix,
 *   which means a value was cast past the type system.
 */
export function reportTypeFromOption(option: ReportsSidebarOption): ReportType | null {
  if (!option.startsWith(TYPE_OPTION_PREFIX)) {
    return null
  }
  const value: string = option.slice(TYPE_OPTION_PREFIX.length)
  if (!REPORT_TYPE_VALUE_SET.has(value)) {
    throw new Error(`Unknown report type in sidebar option: "${option}"`)
  }
  return value as ReportType
}

/**
 * Converts a ReportsSidebarOption into the corresponding URL path.
 *
 * @param option - The sidebar option to convert.
 * @returns The absolute URL path (e.g. "/reports/type/household-survey").
 */
export function reportsSidebarOptionToPath(option: ReportsSidebarOption): string {
  // Default option maps to the bare base path
  if (option === DEFAULT_REPORTS_SIDEBAR_OPTION) {
    return BASE_PATH
  }

  if (option === "recently-added") {
    return `${BASE_PATH}/recently-added`
  }

  if (option === "needs-review") {
    return NEEDS_REVIEW_PATH
  }

  // Remaining options are all report types → /reports/type/:slug.
  // reportTypeFromOption throws on a malformed option, so null cannot occur here
  // for a well-typed value; the check keeps the compiler and reader honest.
  const reportType: ReportType | null = reportTypeFromOption(option)
  if (reportType === null) {
    throw new Error(`Unhandled reports sidebar option: "${option}"`)
  }
  return `${BASE_PATH}/type/${REPORT_TYPE_TO_SLUG[reportType]}`
}

/**
 * Derives the current ReportsSidebarOption from the browser's location pathname.
 *
 * Detail pages (/reports/:biblioId), unknown segments, and unknown type slugs
 * all resolve to the default, matching how unknown Journal Articles topic
 * slugs behave.
 *
 * @param pathname - The current location.pathname (e.g. "/reports/type/household-survey").
 * @returns The resolved ReportsSidebarOption, defaulting to "all-reports" if unrecognized.
 */
export function pathToReportsSidebarOption(pathname: string): ReportsSidebarOption {
  // Strip trailing slash for consistency
  const normalized: string = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname

  if (normalized === `${BASE_PATH}/recently-added`) {
    return "recently-added"
  }

  if (normalized === NEEDS_REVIEW_PATH) {
    return "needs-review"
  }

  // /reports/type/:slug
  const typeMatch: RegExpMatchArray | null = normalized.match(/^\/reports\/type\/([^/]+)$/)
  if (typeMatch) {
    const reportType: ReportType | undefined = SLUG_TO_REPORT_TYPE.get(typeMatch[1])
    if (reportType !== undefined) {
      return `${TYPE_OPTION_PREFIX}${reportType}` as ReportsSidebarOption
    }
  }

  return DEFAULT_REPORTS_SIDEBAR_OPTION
}
